import type { AuthUser } from '../auth/authProvider'

/** The server could not be reached or answered with a transient error. Never a reason to sign anyone out. */
export class NetworkError extends Error {
  constructor(message = 'The sign-in service could not be reached.') {
    super(message)
    this.name = 'NetworkError'
  }
}

export type SessionLookup = { status: 'ok'; user: AuthUser; expiresAt: number | null } | { status: 'invalid' }
export type TokenLookup = { status: 'ok'; token: string; expiresAt: number } | { status: 'invalid' }
export type RedeemResult = { status: 'ok'; bearer: string } | { status: 'rejected' }

export interface AuthApi {
  getSession(bearer: string): Promise<SessionLookup>
  getToken(bearer: string): Promise<TokenLookup>
  redeem(input: { attempt: string; code: string; verifier: string }): Promise<RedeemResult>
  signOut(bearer: string): Promise<void>
  startUrl(input: { attempt: string; challenge: string; to?: string }): string
}

/** Expiry (epoch ms) of a JWT, read from its payload. The signature is the database's business, not ours. */
export function jwtExpiry(token: string): number {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]!.replace(/-/g, '+').replace(/_/g, '/'))) as { exp?: unknown }
    if (typeof payload.exp === 'number') return payload.exp * 1000
  } catch { /* fall through */ }
  return 0
}

function mapUser(raw: Record<string, unknown>): AuthUser | null {
  if (typeof raw.id !== 'string' || !raw.id) return null
  const email = typeof raw.email === 'string' && raw.email ? raw.email : null
  const name = typeof raw.name === 'string' && raw.name ? raw.name : email ?? 'miniMany user'
  return { id: raw.id, name, email, avatarUrl: typeof raw.image === 'string' && raw.image ? raw.image : null }
}

export function createAuthApi(authUrl: string, fetchImpl: typeof fetch = (...args) => fetch(...args)): AuthApi {
  const bearerHeaders = (bearer: string) => ({ authorization: `Bearer ${bearer}` })
  async function request(url: string, init: RequestInit): Promise<Response> {
    let response: Response
    try { response = await fetchImpl(url, { ...init, credentials: 'omit', cache: 'no-store' }) } catch { throw new NetworkError() }
    // Server trouble and rate limiting are transient: the person stays signed in and the caller tries again later.
    if (response.status >= 500 || response.status === 429) throw new NetworkError(`The sign-in service answered ${response.status}.`)
    return response
  }

  return {
    async getSession(bearer) {
      const response = await request(`${authUrl}/api/auth/get-session`, { headers: bearerHeaders(bearer) })
      if (!response.ok) return { status: 'invalid' }
      const body = (await response.json().catch(() => null)) as { user?: Record<string, unknown>; session?: { expiresAt?: unknown } } | null
      const user = body?.user ? mapUser(body.user) : null
      if (!user) return { status: 'invalid' }
      const expires = typeof body?.session?.expiresAt === 'string' ? Date.parse(body.session.expiresAt) : NaN
      return { status: 'ok', user, expiresAt: Number.isFinite(expires) ? expires : null }
    },
    async getToken(bearer) {
      const response = await request(`${authUrl}/api/auth/token`, { headers: bearerHeaders(bearer) })
      if (!response.ok) return { status: 'invalid' }
      const body = (await response.json().catch(() => null)) as { token?: unknown } | null
      if (typeof body?.token !== 'string' || !body.token) return { status: 'invalid' }
      return { status: 'ok', token: body.token, expiresAt: jwtExpiry(body.token) }
    },
    async redeem(input) {
      const response = await request(`${authUrl}/handoff/redeem`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      })
      const bearer = response.headers.get('set-auth-token')
      if (!response.ok || !bearer) return { status: 'rejected' }
      return { status: 'ok', bearer }
    },
    async signOut(bearer) {
      await request(`${authUrl}/api/auth/sign-out`, { method: 'POST', headers: { ...bearerHeaders(bearer), 'content-type': 'application/json' }, body: '{}' })
    },
    startUrl({ attempt, challenge, to }) {
      const url = new URL(`${authUrl}/handoff/start`)
      url.searchParams.set('attempt', attempt)
      url.searchParams.set('cc', challenge)
      if (to) url.searchParams.set('to', to)
      return url.toString()
    },
  }
}
